/**
 * A region as a zone's map: the regions by id, and the chunked GameMap the `generateZone` hook
 * returns for a zone with `region` set (lane A's `chunkedMap` over the region's generator).
 *
 * On by default (docs/handoff/region-on.md): `yanyu` is the 3,072 m region with the village stamped
 * at its core. `setRegionMaps(false)` gives the authored village map back (tests, tools).
 */
import { generateZone, zoneHooks, type GameMap, type Warp } from '../map';
import { linksOf, ZONE_LIST, ZONES, type Side, type ZoneId } from '../data/zones';
import { YANYU_REGION } from '../data/regions/yanyu';
import { YANYU_BLOCKOUT } from '../data/regions/yanyu.blockout';
import { JIUZHOU_BLOCKOUT, JIUZHOU_REGION } from '../data/regions/jiuzhou';
import { ZHUMING_BLOCKOUT, ZHUMING_REGION } from '../data/regions/zhuming';
import { chunkedMap, LazyChunkStore } from './chunks';
import { createRegion, type BuiltRegion } from './generate';
import type { BlockoutBox, RegionSketch } from './sketch';
import { markCore } from './core';
import { shiftMap, stampOf } from './stamp';

export { coreOf, fromCore, markCore, toCore } from './core';

/** Every region's sketch and boxes, by id (`ZoneDef.region`). */
export const REGIONS: Readonly<Record<string, { readonly sketch: RegionSketch; readonly blockout: readonly BlockoutBox[] }>> = {
  yanyu: { sketch: YANYU_REGION, blockout: YANYU_BLOCKOUT },
  /** แคว้นเก้ากระบี่: the same region mirrored (sea south) around the new Yanyu (data/regions/jiuzhou.ts). */
  jiuzhou: { sketch: JIUZHOU_REGION, blockout: JIUZHOU_BLOCKOUT },
  /** จู๋หมิง, the 1 km starter region (data/regions/zhuming.ts). */
  zhuming: { sketch: ZHUMING_REGION, blockout: ZHUMING_BLOCKOUT },
};

const built = new Map<string, BuiltRegion>();

/**
 * A region built once per process: its core is the stamped zone's authored map. `core` may be
 * passed (the village GameMap) to avoid going back through `generateZone`.
 */
export function regionOf(id: string, core?: GameMap): BuiltRegion {
  let r = built.get(id);
  if (!r) {
    const def = REGIONS[id];
    if (!def) throw new Error(`no region ${id}`);
    const s = def.sketch;
    const village = core ?? villageOf(s.core.layout as ZoneId);
    r = createRegion(s, def.blockout, stampOf(village, s.core.x, s.core.y));
    built.set(id, r);
  }
  return r;
}

/**
 * The authored zone map stamped as a region's core, never the region itself (also for tests and
 * tools that check the authored layout, e.g. its 3D heights, whether region maps are on or off).
 */
export function villageOf(zone: ZoneId, seed?: number): GameMap {
  const was = enabled;
  enabled = false;
  try {
    return generateZone(zone, seed);
  } finally {
    enabled = was;
  }
}

let enabled = true;
/** Whether zones with a `region` get the region's map (on by default; off for the authored map alone). */
export function setRegionMaps(on: boolean): void {
  enabled = on;
}
export const regionMapsOn = (): boolean => enabled;

let storeCap: number | undefined;
/**
 * Bounds the chunks a region map built by `generateZone` keeps (lane A's LRU; the client sets it,
 * the server keeps every chunk touched). Undefined for no bound.
 */
export function setRegionCap(cap: number | undefined): void {
  storeCap = cap;
}

/** The region's warps: the sketch's portals at the future sites (the village's own are replaced). */
function portalsOf(s: RegionSketch): Warp[] {
  return s.portals.map((p) => ({ to: p.to, side: p.side, x: p.x, y: p.y }));
}

/**
 * The zone's map for a region (the `generateZone` hook), or undefined when region maps are off.
 * `village` builds the core zone's authored map.
 */
export function regionZoneMap(zone: ZoneId, id: string | undefined, village: () => GameMap): GameMap | undefined {
  if (!enabled || !id) return undefined;
  const v = village();
  const r = regionOf(id, v);
  return regionMap(r, zone, v, storeCap);
}

/**
 * The region's map: chunked (lane A's `chunkedMap` over a `LazyChunkStore` of the region's
 * generator), with the core's structures, flights, landmarks, regions and props moved by its origin.
 * `cap` bounds the chunks kept built (the client's LRU); the server keeps every chunk touched.
 */
export function regionMap(r: BuiltRegion, zone: ZoneId, village: GameMap, cap?: number): GameMap {
  const sh = shiftMap(village, r.core.x, r.core.y);
  const z = ZONES[zone];
  const m = chunkedMap(new LazyChunkStore(r.generate, { chunks: r.sketch.nc, ...(cap ? { cap } : {}) }), {
    seed: village.seed,
    ...sh,
    ...(r.sketch.s.landmarks
      ? { landmarks: { ...sh.landmarks, ...Object.fromEntries(Object.entries(r.sketch.s.landmarks).map(([k, [x, y]]) => [k, { x, y }])) } }
      : {}),
    portals: portalsOf(r.sketch.s),
    biome: z.terrain,
    zone,
    kind: z.kind,
    ...(z.tint ? { tint: z.tint } : {}),
    layout: village.layout,
  });
  markCore(m, r.core);
  return m;
}

const OPPOSITE = { n: 's', s: 'n', e: 'w', w: 'e' } as const;

/**
 * The warps back into a region from a generated zone (while region maps are on): a zone the region
 * has a portal to, and no grid link to it, gets one on the edge facing that portal, so stepping
 * through lands the player beside the portal (`World`'s arrival by `arriveFrom`). Plains and Wudang
 * already border the region's zone and keep their warps.
 */
export function regionReturns(zone: ZoneId): { side: Side; to: ZoneId }[] {
  if (!enabled) return [];
  const linked = linksOf(zone);
  // Only the zones on the grid: the old village (`yanyu`, kept off it) has no way in.
  return ZONE_LIST.flatMap((z) => {
    const p = z.region && z.layout ? REGIONS[z.region]?.sketch.portals.find((q) => q.to === zone) : undefined;
    if (!p || z.id === zone || linked.some((l) => l.to === z.id)) return [];
    return [{ side: OPPOSITE[p.side], to: z.id }];
  });
}

zoneHooks.region = regionZoneMap;
zoneHooks.returns = regionReturns;
